'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import type { Post } from '@/lib/api'
import { getCategoryColor } from '@/lib/constants'
import { useVideoModal } from './VideoModalProvider'

interface VideoCardProps {
  video: Post
  variant?: 'default' | 'compact'
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  const { openModal } = useVideoModal()
  const [isHovered, setIsHovered] = useState(false)
  const locale = useLocale()
  const t = useTranslations('categories')
  const colors = getCategoryColor(video.category)
  const href = `/${locale}/${video.category}/${video.slug}`
  
  // Utiliser le nom dynamique de la catégorie si disponible, sinon fallback sur les traductions statiques
  const getCategoryName = () => {
    if (video.category_name) {
      return video.category_name
    }
    // Fallback sur les traductions statiques pour les catégories existantes
    try {
      return t(video.category)
    } catch {
      // Si la traduction n'existe pas, retourner le slug en format lisible
      return video.category.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const formatDate = (date: string) => {
    const d = new Date(date)
    return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' })
  }

  if (variant === 'compact') {
    return (
      <>
        <article 
          className="group cursor-pointer"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="relative aspect-[16/9] overflow-hidden rounded-xl bg-gray-100">
            <Image
              src={video.coverImage}
              alt={video.title}
              fill
              className="object-cover transition-all duration-500 group-hover:scale-110"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            />
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            
            {/* Duration */}
            {video.duration && (
              <div className="absolute bottom-3 right-3 rounded bg-black/75 px-2 py-1 text-xs font-medium text-white">
                {video.duration}
              </div>
            )}

            {/* Hover Play Button */}
            {isHovered && (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    openModal(video.youtubeId!, video.title)
                  }}
                  className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 backdrop-blur-sm"
                  aria-label="Regarder la vidéo"
                >
                  <svg className="h-6 w-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              </div>
            )}

            {/* YouTube Badge */}
            <div className="absolute left-3 top-3 flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-medium text-white">
              <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
              </svg>
              YouTube
            </div>

            {/* Title and metadata overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <Link href={href}>
                <h3 className="mb-2 line-clamp-2 text-base font-semibold text-white transition-colors hover:text-gray-200">
                  {video.title}
                </h3>
              </Link>
              
              <div className="flex items-center justify-between text-xs text-gray-300">
                <span>{formatDate(video.publishedAt)}</span>
                
              </div>
            </div>
          </div>
        </article>


      </>
    )
  }

  // Default variant
  return (
    <>
      <article 
        className="group overflow-hidden rounded-2xl bg-white shadow-sm transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
          <Image
            src={video.coverImage}
            alt={video.title}
            fill
            className="object-cover transition-all duration-500 group-hover:scale-110"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
          />
          
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
          
          {/* Duration */}
          {video.duration && (
            <div className="absolute bottom-3 right-3 rounded bg-black/75 px-2 py-1 text-sm font-medium text-white">
              {video.duration}
            </div>
          )}

          {/* Play Button - Toujours visible sur mobile, au hover sur desktop */}
          <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-100 md:opacity-0'
          }`}>
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                openModal(video.youtubeId!, video.title)
              }}
              onTouchStart={(e) => {
                // Empêche le double-tap sur mobile
                e.stopPropagation()
              }}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg transition-all hover:bg-red-700 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-red-500/50 backdrop-blur-sm active:scale-95"
              aria-label="Regarder la vidéo"
            >
              <svg className="h-8 w-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
          </div>

          {/* YouTube Badge */}
          <div className="absolute left-3 top-3 flex items-center gap-1 rounded bg-red-600 px-2 py-1 text-xs font-medium text-white">
            <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
            </svg>
            YouTube
          </div>
        </div>
        
        <div className="p-5">
          {/* Category Badge */}
          <div className={`mb-3 inline-flex items-center gap-2 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${colors.text} ${colors.border}`}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${colors.bg}`}></span>
            {getCategoryName()}
          </div>
          
          <Link href={href}>
            <h3 className="mb-3 line-clamp-2 text-xl font-bold leading-tight text-gray-900 transition-colors hover:text-blue-600 cursor-pointer">
              {video.title}
            </h3>
          </Link>
          
          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>{formatDate(video.publishedAt)}</span>
            
          </div>
        </div>
      </article>


    </>
  )
}